import { unitCostCents } from '@Purchasing/Utils/quoteCost'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { TooltipProvider } from '@/components/ui/tooltip'
import { usePermissions } from '@Core/Hooks/usePermissions'
import { cn } from '@/lib/utils'
import { PermissionWrapper } from '@Core/Components/PermissionWrapper/PermissionWrapper'
import { SupplierQuote } from '@Purchasing/Domain/Model/PurchaseProcess'
import { roles } from '@Purchasing/roles'
import { formatDateOnly } from '@Purchasing/Utils/date'
import { formatBRL, formatLeadTime } from '@Purchasing/Utils/money'
import { Ban, ChevronDown, Clock3, Crown, Inbox, MoreHorizontal, Trash2, XCircle } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { supplierNameOf } from './SelectQuoteModal'

// Badges do DS: recebida teal, solicitada amarelo, rejeitada rosa, cancelada neutra.
const statusStyle: Record<string, { label: string; className: string }> = {
    REQUESTED: { label: 'Solicitada', className: 'bg-warning-100 text-warning-600' },
    RECEIVED: { label: 'Recebida', className: 'bg-primary text-primary-foreground' },
    REJECTED: { label: 'Rejeitada', className: 'bg-od-secondary-100 text-od-secondary-600' },
    CANCELED: { label: 'Cancelada', className: 'bg-muted text-muted-foreground' },
}

// Uma única estrutura: grade de colunas no desktop, blocos empilhados no mobile.
// A grade só entra quando cabe (xl); abaixo disso cada cotação é um cartão.
// A coluna de ações tem largura fixa para o cabeçalho e as linhas alinharem.
const COLUMNS = 'xl:grid-cols-[minmax(170px,2fr)_minmax(120px,1.1fr)_minmax(110px,1fr)_minmax(64px,0.5fr)_minmax(120px,1.2fr)_216px]'

const extrasOf = (q: SupplierQuote) =>
    (q.setupFeeCents ?? 0) + (q.shippingFeeCents ?? 0) + (q.taxCents ?? 0) + (q.otherCostsCents ?? 0)

const Cell = ({ label, children, className, hideLabel }: { label: string; children: ReactNode; className?: string; hideLabel?: boolean }) => (
    <div className={cn('min-w-0', className)}>
        {!hideLabel && <span className="block text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground xl:hidden">{label}</span>}
        {children}
    </div>
)

interface QuoteTableProps {
    quotes: SupplierQuote[]
    selectedSupplierQuoteId?: string | null
    suggestedQuantity?: number
    readOnly?: boolean
    winnerLocked?: boolean
    onSelect: (quote: SupplierQuote) => void
    onReceive: (quote: SupplierQuote) => void
    onReject: (quote: SupplierQuote) => void
    onCancel: (quote: SupplierQuote) => void
    onRemove: (quote: SupplierQuote) => void
}

export const QuoteTable = ({
    quotes, selectedSupplierQuoteId, suggestedQuantity, readOnly = false, winnerLocked = false,
    onSelect, onReceive, onReject, onCancel, onRemove,
}: QuoteTableProps) => {
    const [expanded, setExpanded] = useState<string | null>(null)
    const [quoteToRemove, setQuoteToRemove] = useState<SupplierQuote | null>(null)
    const { hasPermission } = usePermissions()
    const canChangeStatus = hasPermission(roles.quote.permissions.status.key)
    const canRemove = hasPermission(roles.quote.permissions.update.key)

    // Destaques só entre cotações RECEBIDAS (solicitada ainda não tem preço) e
    // pelo custo unitário: totais de quantidades diferentes não se comparam.
    const { cheapestId, fastestId } = useMemo(() => {
        const live = quotes.filter((q) => q.status === 'RECEIVED' && q.totalCents > 0 && q.quantity > 0)
        if (live.length < 2) return { cheapestId: null, fastestId: null }
        const cheapest = [...live].sort((a, b) => a.totalCents / a.quantity - b.totalCents / b.quantity)[0]
        const withLead = live.filter((q) => q.leadTimeDays != null)
        const fastest = withLead.length > 1 ? [...withLead].sort((a, b) => (a.leadTimeDays ?? 0) - (b.leadTimeDays ?? 0))[0] : null
        return { cheapestId: cheapest?._id ?? null, fastestId: fastest?._id ?? null }
    }, [quotes])

    return (
        <TooltipProvider delayDuration={200}>
            <div className="overflow-hidden rounded-md border border-border" role="table" aria-label="Comparação de cotações">
                <div role="row" className={cn('hidden gap-3 bg-primary px-3 py-2 text-[11px] font-bold uppercase tracking-[0.04em] text-primary-foreground xl:grid', COLUMNS)}>
                    <span role="columnheader">Fornecedor</span>
                    <span role="columnheader">Total</span>
                    <span role="columnheader">Custo unitário</span>
                    <span role="columnheader">Prazo</span>
                    <span role="columnheader">Condições</span>
                    <span role="columnheader" className="text-right">Ações</span>
                </div>

                {quotes.map((quote, index) => {
                    const isWinner = quote._id === selectedSupplierQuoteId
                    const status = statusStyle[quote.status] ?? { label: quote.status, className: 'bg-muted text-muted-foreground' }
                    const closed = quote.status === 'REJECTED' || quote.status === 'CANCELED'
                    const canDecide = !isWinner && (quote.status === 'REQUESTED' || quote.status === 'RECEIVED')
                    const qtyDiffers = suggestedQuantity != null && quote.quantity !== suggestedQuantity
                    const extras = extrasOf(quote)
                    const awaiting = quote.status === 'REQUESTED'
                    const isOpen = expanded === quote._id

                    return (
                        <div key={quote._id} data-testid={`quote-row-${quote._id}`}
                            className={cn('border-t border-border first:border-t-0 xl:first-of-type:border-t-0', index % 2 === 1 && 'bg-secondary/60', isWinner && 'bg-accent', closed && 'text-muted-foreground')}>
                            <div role="row" className={cn('grid grid-cols-2 items-center gap-3 border-l-4 px-3 py-3 text-sm sm:grid-cols-4 xl:py-2.5', COLUMNS, isWinner ? 'border-l-brand' : 'border-l-transparent')}>
                                <Cell label="Fornecedor" hideLabel className="col-span-2 sm:col-span-4 xl:col-span-1">
                                    <div className="flex min-w-0 items-center gap-2">
                                        <button type="button" aria-expanded={isOpen} aria-label={`${isOpen ? 'Ocultar' : 'Ver'} detalhes de ${supplierNameOf(quote)}`}
                                            onClick={() => setExpanded(isOpen ? null : quote._id)}
                                            className="shrink-0 rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-brand-deep">
                                            <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} strokeWidth={1.5} />
                                        </button>
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                {isWinner && (
                                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase text-brand-deep" data-testid={`winner-badge-${quote._id}`}>
                                                        <Crown className="h-3.5 w-3.5" strokeWidth={1.5} />Vencedora
                                                    </span>
                                                )}
                                                <span className="truncate font-semibold text-foreground" title={supplierNameOf(quote)}>{supplierNameOf(quote)}</span>
                                            </div>
                                            <div className="mt-0.5 flex flex-wrap gap-1">
                                                <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', status.className)}>{status.label}</span>
                                                {quote._id === cheapestId && <span className="rounded-full bg-warning-100 px-2 py-0.5 text-[11px] font-semibold text-warning-600">Menor preço</span>}
                                                {quote._id === fastestId && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground">Menor prazo</span>}
                                            </div>
                                        </div>
                                    </div>
                                </Cell>
                                <Cell label="Total">
                                    {awaiting ? (
                                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-warning-600">
                                            <Clock3 className="h-4 w-4" strokeWidth={1.5} />Aguardando resposta
                                        </span>
                                    ) : (
                                        <span className={cn('block font-display text-base font-extrabold', closed ? 'text-muted-foreground line-through decoration-1' : 'text-brand-deep')}>{formatBRL(quote.totalCents)}</span>
                                    )}
                                    <span className="block text-xs text-muted-foreground" title={qtyDiffers ? 'Quantidade cotada difere da necessidade do processo' : undefined}>
                                        {awaiting ? `${quote.quantity} un. solicitadas` : `${quote.quantity} × ${formatBRL(quote.unitPriceCents)}`}
                                        {qtyDiffers && <span className="font-semibold text-warning-600"> *</span>}
                                    </span>
                                </Cell>
                                <Cell label="Custo unitário">
                                    {awaiting ? <span className="text-muted-foreground">-</span> : (
                                        <>
                                            <span className="block font-semibold tabular-nums">{formatBRL(unitCostCents(quote.totalCents, quote.quantity))}</span>
                                            <span className="block text-xs text-muted-foreground">{extras ? `inclui ${formatBRL(extras)} extras` : 'sem extras'}</span>
                                        </>
                                    )}
                                </Cell>
                                <Cell label="Prazo">{formatLeadTime(quote.leadTimeDays)}</Cell>
                                <Cell label="Condições">
                                    <span className="line-clamp-2" title={quote.paymentTerms}>{quote.paymentTerms || '-'}</span>
                                    {quote.validUntil && <span className="block text-xs text-muted-foreground">válida até {formatDateOnly(quote.validUntil)}</span>}
                                </Cell>
                                <div className="col-span-2 flex items-center justify-end gap-1 sm:col-span-4 xl:col-span-1">
                                    {!readOnly && (
                                        <>
                                            {awaiting && (
                                                <PermissionWrapper permission={roles.quote.permissions.status.key}>
                                                    <Button type="button" size="sm" className="h-8 gap-1.5 whitespace-nowrap px-3" onClick={() => onReceive(quote)}>
                                                        <Inbox className="h-4 w-4" strokeWidth={1.5} />Registrar recebimento
                                                    </Button>
                                                </PermissionWrapper>
                                            )}
                                            {quote.status === 'RECEIVED' && !isWinner && !winnerLocked && (
                                                <PermissionWrapper permission={roles.quote.permissions.select.key}>
                                                    <Button type="button" size="sm" className="h-8 gap-1.5 whitespace-nowrap px-3" onClick={() => onSelect(quote)}>
                                                        <Crown className="h-4 w-4" strokeWidth={1.5} />Selecionar
                                                    </Button>
                                                </PermissionWrapper>
                                            )}
                                            {((canDecide && canChangeStatus) || (!isWinner && !closed && canRemove)) && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button type="button" variant="ghost" size="icon" aria-label={`Mais ações para ${supplierNameOf(quote)}`}
                                                            className="h-8 w-8 rounded-full text-muted-foreground hover:bg-accent hover:text-brand-deep">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="z-[110]">
                                                        {canDecide && canChangeStatus && (
                                                            <>
                                                                <DropdownMenuItem onSelect={() => onReject(quote)}>
                                                                    <XCircle className="mr-2 h-4 w-4" strokeWidth={1.5} />Rejeitar
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onSelect={() => onCancel(quote)}>
                                                                    <Ban className="mr-2 h-4 w-4" strokeWidth={1.5} />Cancelar
                                                                </DropdownMenuItem>
                                                            </>
                                                        )}
                                                        {!isWinner && !closed && canRemove && (
                                                            <>
                                                                {canDecide && canChangeStatus && <DropdownMenuSeparator />}
                                                                <DropdownMenuItem className="text-od-secondary-600 focus:text-od-secondary-600" onSelect={() => setQuoteToRemove(quote)}>
                                                                    <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} />Remover
                                                                </DropdownMenuItem>
                                                            </>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {isOpen && (
                                <div className="grid grid-cols-2 gap-x-6 gap-y-1 border-t border-dashed border-border px-10 py-3 text-xs sm:grid-cols-4" data-testid={`quote-details-${quote._id}`}>
                                    <p><span className="text-muted-foreground">Unitário </span><strong>{formatBRL(quote.unitPriceCents)}</strong></p>
                                    <p><span className="text-muted-foreground">Setup </span>{formatBRL(quote.setupFeeCents)}</p>
                                    <p><span className="text-muted-foreground">Frete </span>{formatBRL(quote.shippingFeeCents)}</p>
                                    <p><span className="text-muted-foreground">{quote.taxPercent != null ? `DIFAL (${String(quote.taxPercent).replace('.', ',')}%)` : 'Impostos'} </span>{formatBRL(quote.taxCents)}</p>
                                    <p><span className="text-muted-foreground">Outros </span>{formatBRL(quote.otherCostsCents)}</p>
                                    <p><span className="text-muted-foreground">Custo unitário final </span><strong>{formatBRL(unitCostCents(quote.totalCents, quote.quantity))}</strong></p>
                                    <p><span className="text-muted-foreground">Quantidade </span>{quote.quantity} un.{qtyDiffers && <span className="text-warning-600"> (necessidade: {suggestedQuantity})</span>}</p>
                                    {quote.notes && <p className="col-span-full text-muted-foreground">{quote.notes}</p>}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            <AlertDialog open={Boolean(quoteToRemove)} onOpenChange={(open) => { if (!open) setQuoteToRemove(null) }}>
                <AlertDialogContent className="rounded-card">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover a cotação de {quoteToRemove ? supplierNameOf(quoteToRemove) : ''}?</AlertDialogTitle>
                        <AlertDialogDescription>Ela sai da comparação. O histórico do processo registra a remoção.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Voltar</AlertDialogCancel>
                        <AlertDialogAction className="rounded-full bg-destructive text-destructive-foreground hover:bg-od-secondary-500"
                            onClick={() => { if (quoteToRemove) onRemove(quoteToRemove); setQuoteToRemove(null) }}>
                            Remover cotação
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </TooltipProvider>
    )
}
